<script setup>
import { useI18n } from 'vue-i18n';

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    required: true,
  },
  message: {
    type: String,
    required: true,
  },
  confirmText: {
    type: String,
    default: null,
  },
  cancelText: {
    type: String,
    default: null,
  },
  type: {
    type: String,
    default: 'warning', // 'warning', 'danger', 'info'
    validator: value => ['warning', 'danger', 'info'].includes(value),
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['confirm', 'cancel']);

const { t } = useI18n();

const iconClass = {
  warning: 'i-lucide-alert-triangle text-yellow-500',
  danger: 'i-lucide-trash-2 text-red-500',
  info: 'i-lucide-info text-blue-500',
};

const confirmButtonClass = {
  warning: 'bg-yellow-500 hover:bg-yellow-600',
  danger: 'bg-red-500 hover:bg-red-600',
  info: 'bg-woot-500 hover:bg-woot-600',
};
</script>

<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition-opacity duration-200"
      leave-active-class="transition-opacity duration-200"
      enter-from-class="opacity-0"
      leave-to-class="opacity-0"
    >
      <div
        v-if="isOpen"
        class="fixed inset-0 bg-black/50 flex items-center justify-center z-[100]"
        @click.self="!loading && emit('cancel')"
      >
        <Transition
          enter-active-class="transition-all duration-200"
          leave-active-class="transition-all duration-200"
          enter-from-class="opacity-0 scale-95"
          leave-to-class="opacity-0 scale-95"
        >
          <div
            v-if="isOpen"
            class="bg-n-solid-2 rounded-xl shadow-xl w-full max-w-md mx-4 overflow-hidden"
          >
            <!-- Content -->
            <div class="p-6">
              <div class="flex items-start gap-4">
                <div
                  class="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                  :class="{
                    'bg-yellow-100 dark:bg-yellow-900/30': type === 'warning',
                    'bg-red-100 dark:bg-red-900/30': type === 'danger',
                    'bg-blue-100 dark:bg-blue-900/30': type === 'info',
                  }"
                >
                  <span :class="iconClass[type]" class="text-2xl" />
                </div>
                <div class="flex-1">
                  <h3 class="text-lg font-semibold text-n-slate-12 mb-2">
                    {{ title }}
                  </h3>
                  <p class="text-sm text-n-slate-11">
                    {{ message }}
                  </p>
                </div>
              </div>
            </div>

            <!-- Actions -->
            <div
              class="flex items-center justify-end gap-2 px-6 py-4 bg-n-alpha-2 border-t border-n-weak"
            >
              <button
                class="px-4 py-2 text-sm font-medium text-n-slate-12 bg-n-alpha-3 border border-n-weak rounded-lg hover:bg-n-slate-4 disabled:opacity-50"
                :disabled="loading"
                @click="emit('cancel')"
              >
                {{ cancelText || t('OMNI.BOLETOS.CANCEL') }}
              </button>
              <button
                class="px-4 py-2 text-sm font-medium text-white rounded-lg disabled:opacity-50 flex items-center gap-2"
                :class="confirmButtonClass[type]"
                :disabled="loading"
                @click="emit('confirm')"
              >
                <span v-if="loading" class="i-lucide-loader-2 animate-spin" />
                {{ confirmText || t('OMNI.BOLETOS.CONFIRM') }}
              </button>
            </div>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>
